import { format } from 'date-fns';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { SessionStatus } from '@/types';
import {
  Alert,
  Button,
  CircularProgress,
  Icon,
  IconButton,
  Typography,
} from '@/ui';

import CoachRescheduleSession from '../CoachRescheduleSession';
import SessionInfoPopover from '../SessionInfoPopover';
import { dropStartTime, isAdminDraggableSession } from '../adminDragDrop';
import {
  availabilityUnavailableBlocksForDay,
  availabilityWindowsInTimeZone,
  blockPositionForDay,
  bucketEventsByDay,
  bucketIntervalsByDay,
  dayKeyFor,
  dayKeyFromIso,
  formatIsoTime,
  getWeekDays,
  getWeekRange,
  layoutOverlappingBlocks,
  visibleHourWindow,
} from '../calendarGrid';
import {
  CHEM_UNAVAILABLE_BACKGROUND,
  HOUR_ROW_HEIGHT_PX,
  UNAVAILABLE_BACKGROUND,
  WEEK_ALL_DAY_CELL_CLASS,
  WEEK_ALL_DAY_ROW_CLASS,
  WEEK_DAY_COLUMN_CLASS,
  WEEK_DAY_HEADER_CLASS,
  WEEK_DAY_HEADER_DATE_CLASS,
  WEEK_DAY_HEADER_TODAY_CLASS,
  WEEK_DAY_HEADER_WEEKDAY_CLASS,
  WEEK_GRID_TEMPLATE_COLUMNS,
  WEEK_HEADER_ROW_CLASS,
  WEEK_HOUR_GUTTER_CELL_CLASS,
  WEEK_HOUR_LABEL_CLASS,
  WEEK_HOUR_ROW_CLASS,
  WEEK_SESSION_BLOCK_CLASS,
  WEEK_SESSION_DETAIL_CLASS,
  WEEK_SESSION_NAME_CLASS,
} from '../calendarStyles';
import {
  ADMIN_NATIVE_LOAD_ERROR_MESSAGE,
  PARTICIPANT_NATIVE_LOAD_ERROR_MESSAGE,
  useCalendarWindow,
} from '../useCalendarWindow';
import { useSessionActions } from '../useSessionActions';

import type { ScheduleViewProps } from '../types';
import type { AppointmentActionRecord } from '@/features/appointments/appointmentActionRecord';
import type { PlatformAdminLifecycleGuardOptions } from '@/features/appointments/usePlatformAdminAppointmentLifecycleGuard';
import type { Session } from '@/types';
import type { DragEvent, FC, MouseEvent } from 'react';

import {
  actionRecordEndAt,
  actionRecordId,
  actionRecordIsConfirmed,
  actionRecordKey,
  actionRecordLifecycle,
  actionRecordMember,
  actionRecordRailsSession,
  actionRecordSessionSubject,
  actionRecordStartAt,
  actionRecordSubject,
  actionRecordTitle,
  isNativeAppointmentActionRecord,
} from '@/features/appointments/appointmentActionRecord';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAdminRescheduleSessionMutation } from '@/store/api/sessionApi';
import {
  clearRescheduleMutationId,
  getRescheduleMutationId,
  rescheduleMutationFingerprint,
} from '@/utils/rescheduleMutation';

const DAY_START_HOUR = 0;
const DAY_END_HOUR = 24;
const MINUTES_PER_DAY = 24 * 60;
const HOURS = Array.from(
  { length: DAY_END_HOUR - DAY_START_HOUR },
  (_unused, index) => DAY_START_HOUR + index,
);

const hourLabel = (hour: number): string => {
  const period = hour < 12 ? 'am' : 'pm';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}${period}`;
};

const DEFAULT_WEEK_STICKY_CHROME_HEIGHT_PX = 90;

const viewportFrameHeightRem = (
  startHour: number,
  endHour: number,
  stickyChromeHeightPx: number,
): string =>
  `${((endHour - startHour) * HOUR_ROW_HEIGHT_PX + stickyChromeHeightPx) / 16}rem`;

type WeekViewProps = ScheduleViewProps & {
  lifecycleOptions?: PlatformAdminLifecycleGuardOptions;
};

const WeekView: FC<WeekViewProps> = ({
  adminCoachId,
  coachId,
  lifecycleOptions,
  onNavigateWeek,
  onTimeZoneChange,
  selectedMemberId,
  timeZone: fallbackTimeZone,
  viewDate,
}) => {
  const { user: currentUser } = useCurrentUser();
  const canAdminDrag = Boolean(adminCoachId);
  const days = getWeekDays(viewDate, fallbackTimeZone);
  const [rangeStart, rangeEnd] = getWeekRange(viewDate, fallbackTimeZone);
  const {
    actionRecords,
    adminNativeLoadError,
    availabilityWindows: calendarAvailabilityWindows,
    chemistryAvailabilityWindows: calendarChemistryWindows,
    events,
    isLoading,
    participantNativeLoadError,
    timeZone: calendarTimeZone,
  } = useCalendarWindow(
    coachId,
    rangeStart,
    rangeEnd,
    selectedMemberId,
    fallbackTimeZone,
    adminCoachId,
  );
  const timeZone = adminCoachId
    ? (fallbackTimeZone ?? 'UTC')
    : (calendarTimeZone ?? fallbackTimeZone ?? 'UTC');
  // MEN-5421: windows arrive in the coach's availability timezone; the admin
  // grid renders in the viewer's, so re-express them before shading.
  // MEN-5476: null/absent means no chem source (draw nothing); [] means every
  // Chem Call day is off. Only an array is re-expressed.
  const chemistryAvailabilityWindows = useMemo(
    () =>
      calendarChemistryWindows
        ? availabilityWindowsInTimeZone(
            calendarChemistryWindows,
            getWeekDays(viewDate, fallbackTimeZone),
            calendarTimeZone,
            timeZone,
          )
        : null,
    [
      calendarChemistryWindows,
      calendarTimeZone,
      fallbackTimeZone,
      timeZone,
      viewDate,
    ],
  );
  const availabilityWindows = useMemo(
    () =>
      calendarAvailabilityWindows
        ? availabilityWindowsInTimeZone(
            calendarAvailabilityWindows,
            getWeekDays(viewDate, fallbackTimeZone),
            calendarTimeZone,
            timeZone,
          )
        : calendarAvailabilityWindows,
    [
      calendarAvailabilityWindows,
      calendarTimeZone,
      fallbackTimeZone,
      timeZone,
      viewDate,
    ],
  );
  const todayKey = dayKeyFromIso(new Date().toISOString(), timeZone);
  const actions = useSessionActions(timeZone, {
    adminCoachId,
    disabled: adminNativeLoadError || participantNativeLoadError,
  });

  useEffect(() => {
    if (adminCoachId) return;
    onTimeZoneChange?.(calendarTimeZone);
  }, [adminCoachId, calendarTimeZone, onTimeZoneChange]);

  // MEN-4695 slice 4: the calendar grid iterates AppointmentActionRecords —
  // a discriminated union of legacy Sessions and native Appointments — so
  // native Appointments (chemistry calls, unpaired coaching Appointments)
  // render as clickable sage-green cards alongside legacy Sessions instead
  // of the raw yellow ExternalCalendarEvent block. Cancelled Sessions
  // continue to be excluded from the grid (List view still renders them
  // with a "Cancelled by ..." pill).
  //
  // MEN-4695 (review finding, WeekView.tsx:119): natives must gate on
  // BOTH `bookingStatus === 'confirmed'` AND
  // `lifecycleStatus !== 'cancelled'`. A `pending` or `rejected`
  // Appointment is not a real bookable session — showing it as a
  // clickable sage-green card lets the coach open Reschedule/Cancel
  // dialogs against a projection Moments has not confirmed. Same
  // filter applied in MonthView.
  const gridRecords = useMemo(
    () =>
      actionRecords.filter((record) => {
        if (isNativeAppointmentActionRecord(record)) {
          return (
            actionRecordIsConfirmed(record) &&
            actionRecordLifecycle(record) !== 'cancelled'
          );
        }
        return record.status !== SessionStatus.CANCELLED;
      }),
    [actionRecords],
  );
  const recordsByDay = useMemo(
    () =>
      bucketIntervalsByDay(
        gridRecords,
        (record) => actionRecordStartAt(record),
        (record) => actionRecordEndAt(record),
        timeZone,
      ),
    [gridRecords, timeZone],
  );
  const eventsByDay = useMemo(
    () =>
      bucketEventsByDay(
        events.filter((event) => !event.allDay),
        timeZone,
      ),
    [events, timeZone],
  );
  const allDayEventsByDay = useMemo(
    () =>
      bucketEventsByDay(
        events.filter((event) => event.allDay),
        timeZone,
      ),
    [events, timeZone],
  );

  const [popoverRecord, setPopoverRecord] =
    useState<AppointmentActionRecord | null>(null);
  const draggedRecordRef = useRef<AppointmentActionRecord | null>(null);
  const [pendingDrop, setPendingDrop] = useState<{
    record: AppointmentActionRecord;
    startTime: string;
  } | null>(null);
  const [dropError, setDropError] = useState('');
  const [adminReschedule, adminRescheduleResult] =
    useAdminRescheduleSessionMutation();
  const dropAdminAppointment =
    pendingDrop &&
    isNativeAppointmentActionRecord(pendingDrop.record) &&
    pendingDrop.record.source === 'platform-admin-appointment'
      ? pendingDrop.record.authoritative
      : null;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!adminNativeLoadError) return;
    setPopoverRecord(null);
    setAnchorEl(null);
    setPendingDrop(null);
    setDropError('');
    draggedRecordRef.current = null;
  }, [adminNativeLoadError]);
  // MEN-4122: open on the day's first session rather than a fixed hour, so a
  // coach whose sessions all sit in the afternoon does not land on an empty
  // grid. Records arrive after the viewport mounts, so the opening hour has to
  // be re-applied when they land. To avoid fighting the coach, we remember the
  // offset we last set: if the viewport is still sitting where we put it, we
  // may move it again; once the coach has scrolled anywhere else we leave it
  // alone. The ref callback still positions every newly mounted node, since a
  // cache-miss refetch remounts the viewport.
  // MEN-4239: the grid still renders the whole 24-hour day so a coach can
  // scroll out to book something outside their usual hours, but the viewport
  // is sized and positioned to the hours that matter — 8am-6pm, widened to
  // cover availability and anything scheduled. The working day fills the
  // frame on load instead of being a thin slice of a long scroll.
  const { endHour: windowEndHour, startHour: windowStartHour } = useMemo(
    () =>
      visibleHourWindow(
        [
          ...gridRecords.map((record) => ({
            end: actionRecordEndAt(record),
            start: actionRecordStartAt(record),
          })),
          ...events
            .filter((event) => !event.allDay)
            .map((event) => ({ end: event.end, start: event.start })),
        ],
        // MEN-5476: Chem Call hours widen the viewport too. null/[] add nothing.
        [
          ...(availabilityWindows ?? []),
          ...(chemistryAvailabilityWindows ?? []),
        ],
        timeZone,
      ),
    [
      availabilityWindows,
      chemistryAvailabilityWindows,
      events,
      gridRecords,
      timeZone,
    ],
  );

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const stickyChromeRef = useRef<HTMLDivElement | null>(null);
  const openingHourRef = useRef(windowStartHour);
  const appliedScrollRef = useRef<number | null>(null);
  const [stickyChromeHeightPx, setStickyChromeHeightPx] = useState(
    DEFAULT_WEEK_STICKY_CHROME_HEIGHT_PX,
  );
  // The window already starts at or before the earliest thing in view, so
  // opening at its top satisfies MEN-4122 (never land on an empty grid)
  // without scrolling past hours the coach asked to see.
  const openingHour = windowStartHour;
  const initializeViewport = useCallback((viewport: HTMLDivElement | null) => {
    const scrollContainer = viewport;
    viewportRef.current = scrollContainer;
    if (!scrollContainer) return;
    const target = openingHourRef.current * HOUR_ROW_HEIGHT_PX;
    scrollContainer.scrollTop = target;
    appliedScrollRef.current = target;
  }, []);
  useLayoutEffect(() => {
    const stickyChrome = stickyChromeRef.current;
    if (!stickyChrome) return undefined;

    const measureStickyChrome = () => {
      const measuredHeight = Math.ceil(
        stickyChrome.getBoundingClientRect().height ||
          stickyChrome.scrollHeight ||
          DEFAULT_WEEK_STICKY_CHROME_HEIGHT_PX,
      );
      setStickyChromeHeightPx((current) =>
        current === measuredHeight ? current : measuredHeight,
      );
    };

    measureStickyChrome();
    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measureStickyChrome);
    observer.observe(stickyChrome);
    return () => observer.disconnect();
  }, [allDayEventsByDay]);

  useEffect(() => {
    openingHourRef.current = openingHour;
    const scrollContainer = viewportRef.current;
    if (!scrollContainer) return;
    if (
      appliedScrollRef.current !== null &&
      scrollContainer.scrollTop !== appliedScrollRef.current
    )
      return;
    const target = openingHour * HOUR_ROW_HEIGHT_PX;
    scrollContainer.scrollTop = target;
    appliedScrollRef.current = target;
  }, [openingHour]);

  const openPopover = (
    record: AppointmentActionRecord,
    event: MouseEvent<HTMLElement>,
  ) => {
    setPopoverRecord(record);
    setAnchorEl(event.currentTarget);
  };
  const closePopover = () => {
    setPopoverRecord(null);
    setAnchorEl(null);
  };
  const handleDrop = (day: Date, event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedRecord = draggedRecordRef.current;
    if (
      !canAdminDrag ||
      !droppedRecord ||
      pendingDrop ||
      adminNativeLoadError ||
      participantNativeLoadError
    ) {
      return;
    }
    const bounds = event.currentTarget.getBoundingClientRect();
    const minute =
      ((event.clientY - bounds.top) / bounds.height) * MINUTES_PER_DAY;
    try {
      setDropError('');
      setPendingDrop({
        record: droppedRecord,
        startTime: dropStartTime(dayKeyFor(day), minute, timeZone),
      });
    } catch (error) {
      setDropError(
        error instanceof Error ? error.message : 'Invalid drop time.',
      );
    } finally {
      draggedRecordRef.current = null;
    }
  };
  const confirmDrop = async () => {
    if (!canAdminDrag || !adminCoachId || !pendingDrop) return;
    const { record, startTime } = pendingDrop;
    const session = isNativeAppointmentActionRecord(record)
      ? actionRecordRailsSession(record)
      : record;
    if (!session || dropAdminAppointment) return;
    const fingerprint = rescheduleMutationFingerprint([
      'admin-drag',
      adminCoachId,
      session.id,
      session.userId,
      session.startedAt,
      startTime,
    ]);
    const mutationId = getRescheduleMutationId(fingerprint);
    try {
      setDropError('');
      await adminReschedule({
        coachId: adminCoachId,
        id: session.id,
        memberId: session.userId,
        mutationId,
        overrideUnavailable: false,
        scope: 'single',
        startTime,
        viewerTimezone: timeZone,
      }).unwrap();
      clearRescheduleMutationId(fingerprint);
      setPendingDrop(null);
    } catch {
      setDropError('Unable to reschedule this session. Please try again.');
    }
  };

  if (isLoading)
    return <CircularProgress data-testid="schedule-week-loading" />;
  if (adminNativeLoadError) {
    return <Alert severity="error">{ADMIN_NATIVE_LOAD_ERROR_MESSAGE}</Alert>;
  }
  if (participantNativeLoadError) {
    return (
      <Alert severity="error">{PARTICIPANT_NATIVE_LOAD_ERROR_MESSAGE}</Alert>
    );
  }

  return (
    <div className="bg-white" data-testid="schedule-week-view">
      {chemistryAvailabilityWindows ? (
        <ul
          aria-label="Availability legend"
          className="m-0 flex list-none gap-4 px-2 py-1 text-xs"
          data-testid="schedule-week-availability-legend"
        >
          {(
            [
              ['Coaching unavailable', UNAVAILABLE_BACKGROUND],
              ['Chem Call unavailable', CHEM_UNAVAILABLE_BACKGROUND],
            ] as const
          ).map(([label, background]) => (
            <li key={label} className="flex items-center gap-1">
              <span
                aria-hidden="true"
                className="inline-block h-3 w-4 border border-solid border-cement-200"
                style={{ backgroundImage: background }}
              />
              {label}
            </li>
          ))}
        </ul>
      ) : null}
      {/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- Keyboard users need to focus and scroll the overflow region. */}
      <div
        ref={initializeViewport}
        aria-label="Week calendar"
        className="min-h-80 overflow-auto"
        data-testid="schedule-week-viewport"
        role="region"
        style={{
          height: viewportFrameHeightRem(
            windowStartHour,
            windowEndHour,
            stickyChromeHeightPx,
          ),
        }}
        tabIndex={0}
      >
        <div style={{ minWidth: '48rem' }}>
          <div
            ref={stickyChromeRef}
            className="sticky top-0 z-20 bg-white"
            data-testid="schedule-week-sticky-chrome"
          >
            <div
              className={WEEK_HEADER_ROW_CLASS}
              data-testid="schedule-week-header"
              style={{ gridTemplateColumns: WEEK_GRID_TEMPLATE_COLUMNS }}
            >
              <div />
              {days.map((day, index) => {
                const isToday = dayKeyFor(day) === todayKey;
                return (
                  <div
                    key={day.toISOString()}
                    className={
                      isToday
                        ? WEEK_DAY_HEADER_TODAY_CLASS
                        : WEEK_DAY_HEADER_CLASS
                    }
                    data-testid="schedule-week-day-heading"
                    data-today={isToday ? 'true' : undefined}
                  >
                    {/* MEN-4674: week-navigation arrows sit inside the
                        Sunday and Saturday headings, matching the Figma
                        design (they are overlaid so the weekday/date pair
                        stays centred in the column). The toolbar-level
                        arrows always move by month. */}
                    {onNavigateWeek && index === 0 ? (
                      <IconButton
                        aria-label="Previous week"
                        className="absolute left-0 top-1/2 -translate-y-1/2"
                        size="small"
                        onClick={() => onNavigateWeek(-1)}
                      >
                        <Icon>chevron_left</Icon>
                      </IconButton>
                    ) : null}
                    <Typography
                      className={WEEK_DAY_HEADER_WEEKDAY_CLASS}
                      variant="caption"
                    >
                      {format(day, 'EEE').toUpperCase()}
                    </Typography>
                    <Typography
                      className={WEEK_DAY_HEADER_DATE_CLASS}
                      variant="subtitle2"
                    >
                      {format(day, 'd')}
                    </Typography>
                    {onNavigateWeek && index === days.length - 1 ? (
                      <IconButton
                        aria-label="Next week"
                        className="absolute right-0 top-1/2 -translate-y-1/2"
                        size="small"
                        onClick={() => onNavigateWeek(1)}
                      >
                        <Icon>chevron_right</Icon>
                      </IconButton>
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div
              aria-label="All-day events"
              className={WEEK_ALL_DAY_ROW_CLASS}
              data-testid="schedule-week-all-day-row"
              role="region"
              style={{
                gridTemplateColumns: WEEK_GRID_TEMPLATE_COLUMNS,
                maxHeight: '6rem',
              }}
              tabIndex={0}
            >
              <div />
              {days.map((day) => (
                <div
                  key={day.toISOString()}
                  className={WEEK_ALL_DAY_CELL_CLASS}
                >
                  {(allDayEventsByDay.get(dayKeyFor(day)) ?? []).map(
                    (event) => (
                      <div
                        key={event.id}
                        className="truncate rounded bg-yellow-100 px-1 text-xs"
                        data-testid="schedule-week-all-day-event"
                      >
                        Busy
                      </div>
                    ),
                  )}
                </div>
              ))}
            </div>
          </div>

          <div
            className="grid"
            style={{ gridTemplateColumns: WEEK_GRID_TEMPLATE_COLUMNS }}
          >
            <div className="flex flex-col">
              {HOURS.map((hour) => (
                <div key={hour} className={WEEK_HOUR_GUTTER_CELL_CLASS}>
                  <Typography
                    className={WEEK_HOUR_LABEL_CLASS}
                    data-testid="schedule-week-hour-label"
                    variant="caption"
                  >
                    {hourLabel(hour)}
                  </Typography>
                </div>
              ))}
            </div>

            {days.map((day) => {
              const dayRecords = recordsByDay.get(dayKeyFor(day)) ?? [];
              const dayEvents = eventsByDay.get(dayKeyFor(day)) ?? [];
              const recordBlocks = dayRecords
                .map((record) => ({
                  id: actionRecordKey(record),
                  item: record,
                  position: blockPositionForDay(
                    actionRecordStartAt(record),
                    actionRecordEndAt(record),
                    day,
                    DAY_START_HOUR,
                    DAY_END_HOUR,
                    timeZone,
                  ),
                }))
                .filter((block) => block.position.heightPct > 0);
              const eventBlocks = dayEvents
                .map((event) => ({
                  id: `event:${event.id}`,
                  item: event,
                  position: blockPositionForDay(
                    event.start,
                    event.end,
                    day,
                    DAY_START_HOUR,
                    DAY_END_HOUR,
                    timeZone,
                  ),
                }))
                .filter((block) => block.position.heightPct > 0);
              const horizontalLayout = layoutOverlappingBlocks(
                [...eventBlocks, ...recordBlocks].map((block) => ({
                  id: block.id,
                  ...block.position,
                })),
              );
              const unavailableBlocks = availabilityWindows
                ? availabilityUnavailableBlocksForDay(
                    availabilityWindows,
                    day,
                    DAY_START_HOUR,
                    DAY_END_HOUR,
                  )
                : [];
              const chemUnavailableBlocks = chemistryAvailabilityWindows
                ? availabilityUnavailableBlocksForDay(
                    chemistryAvailabilityWindows,
                    day,
                    DAY_START_HOUR,
                    DAY_END_HOUR,
                  )
                : [];
              return (
                <div
                  key={day.toISOString()}
                  className={WEEK_DAY_COLUMN_CLASS}
                  data-testid="schedule-week-day-column"
                  style={{ height: `${HOURS.length * 3.5}rem` }}
                  onDragOver={(event) => {
                    if (canAdminDrag && draggedRecordRef.current)
                      event.preventDefault();
                  }}
                  onDrop={(event) => handleDrop(day, event)}
                >
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 flex flex-col"
                  >
                    {HOURS.map((hour) => (
                      <div
                        key={hour}
                        className={WEEK_HOUR_ROW_CLASS}
                        data-testid="schedule-week-hour-row"
                      />
                    ))}
                  </div>
                  {unavailableBlocks.map((block) => (
                    <div
                      key={`${day.toISOString()}-${block.topPct}-${block.heightPct}`}
                      className="absolute inset-x-0"
                      data-testid="schedule-week-unavailable"
                      style={{
                        backgroundImage: UNAVAILABLE_BACKGROUND,
                        height: `${block.heightPct}%`,
                        top: `${block.topPct}%`,
                      }}
                    />
                  ))}
                  {chemUnavailableBlocks.map((block) => (
                    <div
                      key={`chem-${day.toISOString()}-${block.topPct}-${block.heightPct}`}
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-x-0"
                      data-testid="schedule-week-chem-unavailable"
                      style={{
                        backgroundImage: CHEM_UNAVAILABLE_BACKGROUND,
                        height: `${block.heightPct}%`,
                        top: `${block.topPct}%`,
                      }}
                    />
                  ))}
                  {eventBlocks.map(({ id, item: event, position }) => {
                    const horizontal = horizontalLayout.get(id);
                    return (
                      <div
                        key={event.id}
                        className="absolute overflow-hidden rounded bg-yellow-100 px-1 text-xs"
                        data-testid="schedule-week-event"
                        style={{
                          height: `${position.heightPct}%`,
                          left: `${horizontal?.leftPct ?? 0}%`,
                          top: `${position.topPct}%`,
                          width: `${horizontal?.widthPct ?? 100}%`,
                        }}
                      >
                        Busy
                      </div>
                    );
                  })}
                  {recordBlocks.map(({ id, item: record, position }) => {
                    const horizontal = horizontalLayout.get(id);
                    // Appointment fields drive actions; an exact Rails pair
                    // contributes only the member profile identity.
                    let draggableCandidate: Session | undefined;
                    if (!isNativeAppointmentActionRecord(record)) {
                      draggableCandidate = record;
                    } else if (record.source !== 'platform-admin-appointment') {
                      draggableCandidate = actionRecordRailsSession(record);
                    }
                    const isPlatformAdminNative =
                      isNativeAppointmentActionRecord(record) &&
                      record.source === 'platform-admin-appointment';
                    const isDraggable =
                      !pendingDrop &&
                      (isPlatformAdminNative
                        ? canAdminDrag &&
                          currentUser.id !== record.authoritative.member.id &&
                          record.view.actionCapabilities.reschedule.allowed &&
                          record.view.actionCapabilities.reschedule.scopes.includes(
                            'appointment',
                          )
                        : canAdminDrag &&
                          currentUser.id !== adminCoachId &&
                          draggableCandidate !== undefined &&
                          isAdminDraggableSession(draggableCandidate));
                    return (
                      <button
                        key={actionRecordId(record)}
                        className={WEEK_SESSION_BLOCK_CLASS}
                        data-testid="schedule-week-session"
                        draggable={isDraggable}
                        style={{
                          height: `${position.heightPct}%`,
                          left: `${horizontal?.leftPct ?? 0}%`,
                          top: `${position.topPct}%`,
                          width: `${horizontal?.widthPct ?? 100}%`,
                        }}
                        type="button"
                        onClick={(event) => openPopover(record, event)}
                        onDragEnd={() => {
                          draggedRecordRef.current = null;
                        }}
                        onDragStart={(event) => {
                          // MEN-4695 (pre-PR review finding): the drag
                          // payload MUST be the legacy Rails Session, not
                          // the Appointment-adapted `sessionForClick`.
                          // The drop handler posts to
                          // `/admin/calendar/sessions/:id/reschedule` — a
                          // Rails Session route — with `session.id` as
                          // the session UUID and `session.userId` as the
                          // Rails member id. Using `sessionForClick.id`
                          // would send an Appointment UUID and a person
                          // UUID into a Rails-scoped endpoint (silent
                          // 404). isDraggable === true guarantees
                          // draggableCandidate !== undefined (its
                          // definition includes that check), so the
                          // non-null assertion is safe.
                          if (!isDraggable) {
                            event.preventDefault();
                            return;
                          }

                          const dragSubject = isPlatformAdminNative
                            ? record
                            : draggableCandidate!;
                          event.dataTransfer.setData(
                            'text/plain',
                            actionRecordId(dragSubject),
                          );
                          draggedRecordRef.current = dragSubject;
                        }}
                      >
                        <span className={WEEK_SESSION_NAME_CLASS}>
                          {actionRecordMember(record).displayName}
                        </span>
                        <span className={WEEK_SESSION_DETAIL_CLASS}>
                          {actionRecordTitle(record)}
                        </span>
                        <span className={WEEK_SESSION_DETAIL_CLASS}>
                          {formatIsoTime(actionRecordStartAt(record), timeZone)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}

      {pendingDrop && dropAdminAppointment && (
        <CoachRescheduleSession
          actionSubject={actionRecordSubject(pendingDrop.record)}
          adminCoachId={adminCoachId}
          close={() => setPendingDrop(null)}
          initialStartAt={pendingDrop.startTime}
          lifecycleOptions={lifecycleOptions}
          session={actionRecordSessionSubject(pendingDrop.record)}
          timeZone={timeZone}
        />
      )}
      {pendingDrop && !dropAdminAppointment && (
        <Alert className="m-3" severity="warning">
          <div className="flex items-center gap-3">
            <span>
              Reschedule {actionRecordMember(pendingDrop.record).displayName} to{' '}
              {formatIsoTime(pendingDrop.startTime, timeZone)}?
            </span>
            <Button
              disabled={adminRescheduleResult.isLoading}
              size="small"
              variant="contained"
              onClick={() => confirmDrop().catch(() => undefined)}
            >
              Confirm
            </Button>
            <Button
              disabled={adminRescheduleResult.isLoading}
              size="small"
              onClick={() => {
                setDropError('');
                setPendingDrop(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </Alert>
      )}
      {dropError && <Alert severity="error">{dropError}</Alert>}

      <SessionInfoPopover
        adminMode={Boolean(adminCoachId)}
        anchorEl={anchorEl}
        session={
          popoverRecord ? actionRecordSessionSubject(popoverRecord) : null
        }
        subject={popoverRecord ? actionRecordSubject(popoverRecord) : undefined}
        timeZone={timeZone}
        onCancel={(subject) => {
          const focusTarget = anchorEl;
          closePopover();
          actions
            .openCancel(subject, () => focusTarget?.focus())
            .catch(() => undefined);
        }}
        onClose={closePopover}
        onReschedule={(subject, returnFocus) => {
          closePopover();
          actions.openReschedule(subject, returnFocus);
        }}
      />
      {actions.modals}
    </div>
  );
};

export default WeekView;
